import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CaretRight, FloppyDisk } from '@phosphor-icons/react'
import Stack from '@mui/material/Stack'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import useUnsavedChangesGuard from '../../../components/dialogs/useUnsavedChangesGuard'
import LoadingScreen from '../../../components/LoadingScreen'
import StickyActionBar from '../../../components/StickyActionBar'
import RbacUserDetailHeader from '../../components/RbacUserDetailHeader'
import { isAuthRedirectError } from '../../../auth'
import {
  applyPermissionDependencies,
  applyPresetToScope,
  scopesForModule,
  permissionsToPayload,
  reconcilePermissionsWithCatalog,
  rolePermissionsToMap,
  togglePresetForScope
} from '../../utils/permissions'
import { fetchPermissionCatalog } from '../services/permissionCatalogApi'
import { createRole, fetchRole, fetchRoles, updateRole } from '../services/rolesApi'
import RoleWizardGeneralStep from './RoleWizardGeneralStep'
import RoleWizardPermissionsStep from './RoleWizardPermissionsStep'
import RoleWizardStepProgress from './RoleWizardStepProgress'
import { scopeStateKey, scopeTitle } from './roleWizardConstants'
import {
  buildRbacRoleSavedFeedback,
  RBAC_ROLE_FEEDBACK_STATE_KEY
} from '../shared/rbacRoleNavigationFeedback'

const ROLES_INDEX_PATH = '/configuracion/rbac/roles'
const REQUIRED_ROLE_NAME_MESSAGE = 'Escribe un nombre para el rol antes de continuar.'
const DUPLICATE_ROLE_NAME_MESSAGE = 'Ya existe un rol con este nombre. Elige uno distinto.'
const REQUIRED_ROLE_PERMISSION_MESSAGE = 'Habilita al menos un permiso para guardar el rol.'
const EXIT_WITHOUT_SAVING_TITLE = '¿Salir sin guardar?'
const EXIT_WITHOUT_SAVING_CONFIRM = 'Salir sin guardar'
const EXIT_WITHOUT_SAVING_CANCEL = 'Seguir editando'
const GENERIC_CONNECTION_ERROR_MESSAGE =
  'Ocurrió un error de conexión. Revisa tu internet e intenta nuevamente.'

function normalizePermissionsSnapshot(permissions = {}) {
  return JSON.stringify(
    Object.entries(permissions)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([key, actions]) => [key, [...actions].sort()])
  )
}

function catalogPermissionLabels(catalog) {
  return Object.fromEntries(
    (catalog?.modules || []).flatMap((module) =>
      (module.scopes || []).map((scope) => [
        scopeStateKey(module.key, scope.key),
        scopeTitle(module.key, scope)
      ])
    )
  )
}

function removedPermissionsMessage(keys, labels) {
  const names = keys.map((key) => `«${labels[key] || key.split('|')[1]}»`)
  if (names.length === 1) {
    return `El permiso ${names[0]} fue desactivado para el negocio y se eliminó de la selección.`
  }

  return `Los permisos ${names.join(', ')} fueron desactivados para el negocio y se eliminaron de la selección.`
}

function getRoleWizardErrorMessage(error) {
  const message = String(error?.message || '').trim()
  if (!message) return GENERIC_CONNECTION_ERROR_MESSAGE

  const normalizedMessage = message.toLowerCase()
  if (
    normalizedMessage === 'failed to fetch' ||
    normalizedMessage.includes('networkerror') ||
    normalizedMessage.includes('network error')
  ) {
    return GENERIC_CONNECTION_ERROR_MESSAGE
  }

  return message
}

export default function CreateRbacRoleWizard() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(0)
  const [catalog, setCatalog] = useState(null)
  const [loading, setLoading] = useState(true)
  const [validatingPermissions, setValidatingPermissions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [availabilityWarning, setAvailabilityWarning] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [nameError, setNameError] = useState('')
  const [permissionError, setPermissionError] = useState('')
  const [rolesCatalog, setRolesCatalog] = useState([])
  const [explicitPermissions, setExplicitPermissions] = useState({})
  const [permissionLabels, setPermissionLabels] = useState({})
  const [initialSnapshot, setInitialSnapshot] = useState(null)
  const [exitDialogOpen, setExitDialogOpen] = useState(false)
  const pendingExitRef = useRef(null)
  const saveAbortControllerRef = useRef(null)

  useEffect(() => {
    let cancelled = false

    async function loadWizard() {
      try {
        setLoading(true)
        const [catalogData, rolesData, role] = await Promise.all([
          fetchPermissionCatalog(),
          fetchRoles({ includeInactive: true }),
          isEdit ? fetchRole(id) : Promise.resolve(null)
        ])
        if (cancelled) return
        setCatalog(catalogData)
        setRolesCatalog(Array.isArray(rolesData) ? rolesData : [])
        const labels = {
          ...catalogPermissionLabels(catalogData),
          ...Object.fromEntries(
            (role?.permissions || []).map((permission) => [
              scopeStateKey(permission.module_key, permission.scope_key),
              permission.scope_label || permission.scope_key
            ])
          )
        }
        setPermissionLabels(labels)

        if (role) {
          setName(role?.name || '')
          setDescription(role?.description || '')
          const reconciled = reconcilePermissionsWithCatalog(
            rolePermissionsToMap(role?.permissions),
            catalogData
          )
          setExplicitPermissions(reconciled.permissions)
          if (reconciled.removedKeys.length > 0) {
            setAvailabilityWarning(removedPermissionsMessage(reconciled.removedKeys, labels))
          }
          setInitialSnapshot({
            name: role?.name || '',
            description: role?.description || '',
            permissions: normalizePermissionsSnapshot(reconciled.permissions)
          })
        } else {
          setInitialSnapshot({
            name: '',
            description: '',
            permissions: normalizePermissionsSnapshot({})
          })
        }
        setLoading(false)
      } catch (loadError) {
        if (cancelled || isAuthRedirectError(loadError)) return
        setError(getRoleWizardErrorMessage(loadError))
        setLoading(false)
      }
    }

    loadWizard()
    return () => {
      cancelled = true
    }
  }, [id, isEdit])

  const permissionModules = useMemo(
    () => (catalog?.modules || []).filter((module) => scopesForModule(module).length > 0),
    [catalog]
  )
  const permissionsByScope = useMemo(
    () => applyPermissionDependencies(explicitPermissions, catalog),
    [explicitPermissions, catalog]
  )
  const permissionPayload = useMemo(
    () => permissionsToPayload(permissionsByScope),
    [permissionsByScope]
  )
  const headerActionAlert = useMemo(() => {
    if (error) {
      return {
        id: `role-wizard-error-${error}`,
        severity: 'error',
        message: error
      }
    }

    if (permissionError && activeStep === 1) {
      return {
        id: `role-wizard-permission-error-${permissionError}`,
        severity: 'error',
        message: permissionError
      }
    }

    if (availabilityWarning) {
      return {
        id: `role-wizard-availability-warning-${availabilityWarning}`,
        severity: 'warning',
        message: availabilityWarning
      }
    }

    return null
  }, [activeStep, availabilityWarning, error, permissionError])
  const interactionDisabled = saving || validatingPermissions
  const hasUnsavedChanges = useMemo(() => {
    if (!initialSnapshot) return false

    return (
      name !== initialSnapshot.name ||
      description !== initialSnapshot.description ||
      normalizePermissionsSnapshot(explicitPermissions) !== initialSnapshot.permissions
    )
  }, [description, explicitPermissions, initialSnapshot, name])
  const shouldGuardExit = hasUnsavedChanges || saving
  const exitDialogBody = saving
    ? 'Aún se está guardando este rol. Si sales ahora, el guardado en curso se cancelará.'
    : 'Aún tienes cambios pendientes de guardar. Si sales, estos cambios se perderán.'
  const { allowLeave } = useUnsavedChangesGuard({
    when: shouldGuardExit,
    onBlock: (proceed) => {
      pendingExitRef.current = proceed
      setExitDialogOpen(true)
    }
  })

  useEffect(() => {
    if (permissionPayload.length > 0 && permissionError) {
      setPermissionError('')
    }
  }, [permissionError, permissionPayload.length])

  useEffect(() => {
    return () => {
      saveAbortControllerRef.current?.abort()
    }
  }, [])

  const applyAllModulesPreset = (preset) => {
    setExplicitPermissions((previous) => {
      const next = { ...previous }
      permissionModules.forEach((module) => {
        scopesForModule(module).forEach((scope) => {
          next[scopeStateKey(module.key, scope.key)] = applyPresetToScope(scope.actions, preset)
        })
      })
      return next
    })
  }

  const applyScopePreset = (moduleKey, scope, preset) => {
    setExplicitPermissions((previous) => {
      const key = scopeStateKey(moduleKey, scope.key)

      return {
        ...previous,
        [key]: togglePresetForScope(scope.actions, preset, previous[key] || [])
      }
    })
  }

  const canContinue = activeStep === 0 ? name.trim().length > 0 : permissionPayload.length > 0

  const isRoleNameTakenError = (message = '') => {
    const normalizedMessage = String(message).toLowerCase()
    return normalizedMessage.includes('name has already been taken')
  }

  const roleNameAlreadyExists = (candidateName) => {
    const normalizedCandidate = candidateName.trim().toLowerCase()

    return rolesCatalog.some((role) => {
      if (!role?.name) return false
      if (isEdit && String(role.id) === String(id)) return false
      return role.name.trim().toLowerCase() === normalizedCandidate
    })
  }

  const validateRoleName = async () => {
    const trimmedName = name.trim()

    if (!trimmedName) {
      setNameError(REQUIRED_ROLE_NAME_MESSAGE)
      return false
    }

    if (roleNameAlreadyExists(trimmedName)) {
      setNameError(DUPLICATE_ROLE_NAME_MESSAGE)
      return false
    }

    setNameError('')
    return true
  }

  const handleNameChange = (value) => {
    setName(value)
    if (!nameError) return

    if (!value.trim()) {
      setNameError(REQUIRED_ROLE_NAME_MESSAGE)
      return
    }

    setNameError('')
  }

  const validateAvailablePermissions = async ({ showLoading = true } = {}) => {
    if (showLoading) setValidatingPermissions(true)
    try {
      const freshCatalog = await fetchPermissionCatalog()
      const reconciled = reconcilePermissionsWithCatalog(explicitPermissions, freshCatalog)
      const labels = {
        ...permissionLabels,
        ...catalogPermissionLabels(catalog),
        ...catalogPermissionLabels(freshCatalog)
      }

      setCatalog(freshCatalog)
      setPermissionLabels(labels)
      if (reconciled.removedKeys.length > 0) {
        setExplicitPermissions(reconciled.permissions)
        setAvailabilityWarning(removedPermissionsMessage(reconciled.removedKeys, labels))
        return null
      }

      return {
        catalog: freshCatalog,
        permissions: reconciled.permissions
      }
    } finally {
      if (showLoading) setValidatingPermissions(false)
    }
  }

  const saveRole = async () => {
    const abortController = new AbortController()
    saveAbortControllerRef.current = abortController

    try {
      setError('')
      setSaving(true)
      if (!(await validateRoleName())) return
      if (permissionPayload.length === 0) {
        setPermissionError(REQUIRED_ROLE_PERMISSION_MESSAGE)
        return
      }
      const validated = await validateAvailablePermissions({ showLoading: false })
      if (!validated) return

      const payload = {
        name: name.trim(),
        description: description.trim(),
        permissions: permissionsToPayload(
          applyPermissionDependencies(validated.permissions, validated.catalog)
        )
      }

      const savedRole = isEdit
        ? await updateRole(id, payload, { signal: abortController.signal })
        : await createRole(payload, { signal: abortController.signal })

      allowLeave()
      navigate(ROLES_INDEX_PATH, {
        replace: true,
        state: {
          [RBAC_ROLE_FEEDBACK_STATE_KEY]: buildRbacRoleSavedFeedback({
            isEdit,
            roleId: savedRole?.id || null
          })
        }
      })
    } catch (saveError) {
      if (saveError?.name === 'AbortError' || abortController.signal.aborted) return
      if (isAuthRedirectError(saveError)) return
      if (isRoleNameTakenError(saveError.message)) {
        setNameError(DUPLICATE_ROLE_NAME_MESSAGE)
        setActiveStep(0)
        return
      }
      if (saveError.message?.includes('fue desactivado para el negocio')) {
        try {
          const refreshed = await validateAvailablePermissions({ showLoading: false })
          if (refreshed) {
            setAvailabilityWarning(
              `${getRoleWizardErrorMessage(saveError)}. La lista de permisos disponibles fue actualizada.`
            )
          }
        } catch (refreshError) {
          if (!isAuthRedirectError(refreshError)) {
            setError(getRoleWizardErrorMessage(refreshError))
          }
        }
      } else {
        setError(getRoleWizardErrorMessage(saveError))
      }
    } finally {
      if (saveAbortControllerRef.current === abortController) {
        saveAbortControllerRef.current = null
      }
      setSaving(false)
    }
  }

  const navigateToRolesIndex = () => {
    navigate(ROLES_INDEX_PATH)
  }

  const handleConfirmExit = () => {
    const proceed = pendingExitRef.current

    if (saving) {
      saveAbortControllerRef.current?.abort()
    }

    setExitDialogOpen(false)
    pendingExitRef.current = null

    if (proceed) {
      proceed()
      return
    }

    allowLeave()
    navigateToRolesIndex()
  }

  const handleCloseExitDialog = () => {
    setExitDialogOpen(false)
    pendingExitRef.current = null
  }

  const handlePrimary = async () => {
    if (activeStep === 0) {
      try {
        setError('')
        if (!(await validateRoleName())) return
        const validated = await validateAvailablePermissions()
        if (validated) setActiveStep(1)
      } catch (validationError) {
        if (!isAuthRedirectError(validationError)) {
          setError(getRoleWizardErrorMessage(validationError))
        }
      }
      return
    }

    setPermissionError('')
    saveRole()
  }

  if (loading) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <>
      <RbacUserDetailHeader
        title={isEdit ? 'Editar rol' : 'Crear rol'}
        backLabel="Volver a roles"
        actionAlert={headerActionAlert}
        onBack={navigateToRolesIndex}
        onCloseAlert={() => {
          if (error) {
            setError('')
            return
          }

          if (permissionError && activeStep === 1) {
            setPermissionError('')
            return
          }

          if (availabilityWarning) {
            setAvailabilityWarning('')
          }
        }}
      />

      <StickyActionBar
        title="Define el nombre del rol y configura sus permisos por módulo:"
        hint={saving ? 'Guardando, no cierres la ventana...' : ''}
        onBack={activeStep === 0 ? undefined : () => setActiveStep(0)}
        onCancel={navigateToRolesIndex}
        onPrimaryClick={handlePrimary}
        primaryButtonType="button"
        primaryLabel={activeStep === 0 ? 'Siguiente' : isEdit ? 'Guardar cambios' : 'Guardar rol'}
        primaryIcon={activeStep === 0 ? CaretRight : FloppyDisk}
        primaryDisabled={interactionDisabled}
        isSaving={saving || validatingPermissions}
        hideSecondaryActions={saving}
        savingLabel={validatingPermissions ? 'Validando...' : 'Guardando...'}
      />

      <div className="index-table">
        <div className="show-area show-area--flush">
          {validatingPermissions ? (
            <LoadingScreen text="Cargando..." minHeight="420px" />
          ) : (
            <>
              <Stack spacing={3.5}>
                <RoleWizardStepProgress activeStep={activeStep} />
                {activeStep === 0 ? (
                  <RoleWizardGeneralStep
                    name={name}
                    description={description}
                    nameError={nameError}
                    onNameChange={handleNameChange}
                    onDescriptionChange={setDescription}
                  />
                ) : (
                  <RoleWizardPermissionsStep
                    modules={permissionModules}
                    permissionsByScope={permissionsByScope}
                    onApplyAll={applyAllModulesPreset}
                    onApplyScopePreset={applyScopePreset}
                    readOnly={interactionDisabled}
                  />
                )}
              </Stack>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={exitDialogOpen}
        title={EXIT_WITHOUT_SAVING_TITLE}
        message={exitDialogBody}
        cancelLabel={EXIT_WITHOUT_SAVING_CANCEL}
        confirmLabel={EXIT_WITHOUT_SAVING_CONFIRM}
        disableClose={saving}
        onClose={handleCloseExitDialog}
        onConfirm={handleConfirmExit}
      />
    </>
  )
}
